/**
 * @file scrollable-tabs.tsx
 * @module app/modules/_common/components/scrollable-tabs
 * @description Single-row tab header that scrolls sideways. Arrow buttons and edge fades show
 * only on the side where tabs are hidden, and the active tab is kept in view. Used by the
 * tab-based Procurement and Master pages.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Nav } from 'react-bootstrap';
import '../styles/scrollable-tabs.css';

export interface ScrollableTabItem {
  key: string;
  label: string;
}

interface ScrollableTabsProps<T extends ScrollableTabItem> {
  tabs: ReadonlyArray<T>;
  activeKey: T['key'];
  /** Called with the clicked tab, e.g. to navigate to its route. */
  onSelect: (tab: T) => void;
}

/**
 * Tab header that stays on one row and scrolls when the tabs do not fit.
 * @param {ScrollableTabsProps} props Tabs, active tab key, and click handler.
 * @returns {JSX.Element} Scrollable tab header with arrow buttons.
 */
export const ScrollableTabs = <T extends ScrollableTabItem>({
  tabs,
  activeKey,
  onSelect,
}: ScrollableTabsProps<T>) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /**
   * Shows each arrow only when tabs are hidden on that side.
   */
  const updateScrollArrows = useCallback(() => {
    const tabsEl = tabsRef.current;
    if (!tabsEl) {
      return;
    }
    const maxScrollLeft = tabsEl.scrollWidth - tabsEl.clientWidth;
    setCanScrollLeft(tabsEl.scrollLeft > 1);
    setCanScrollRight(tabsEl.scrollLeft < maxScrollLeft - 1);
  }, []);

  /**
   * Centres the active tab when any part of it is hidden.
   */
  const revealActiveTab = useCallback(() => {
    const tabsEl = tabsRef.current;
    const active = tabsEl?.querySelector<HTMLElement>('.scrollable-tabs-link.active');
    if (!tabsEl || !active) {
      return;
    }
    const left = active.offsetLeft;
    const right = left + active.offsetWidth;
    if (left < tabsEl.scrollLeft || right > tabsEl.scrollLeft + tabsEl.clientWidth) {
      tabsEl.scrollTo({ left: left - (tabsEl.clientWidth - active.offsetWidth) / 2 });
    }
  }, []);

  // The header width changes with the window and when the page scrollbar comes and goes
  // between routes; the browser then clamps scrollLeft, which can clip the active tab.
  useEffect(() => {
    const tabsEl = tabsRef.current;
    if (!tabsEl) {
      return;
    }
    const handleResize = () => {
      revealActiveTab();
      updateScrollArrows();
    };
    handleResize();
    const observer = new ResizeObserver(handleResize);
    observer.observe(tabsEl);
    return () => observer.disconnect();
  }, [revealActiveTab, updateScrollArrows]);

  useEffect(() => {
    revealActiveTab();
  }, [activeKey, revealActiveTab]);

  /**
   * Scrolls the tab header by most of its visible width.
   * @param {number} direction -1 for left, 1 for right.
   */
  const scrollTabs = (direction: -1 | 1) => {
    const tabsEl = tabsRef.current;
    tabsEl?.scrollBy({ left: direction * tabsEl.clientWidth * 0.6 });
  };

  return (
    <div
      className={[
        'scrollable-tabs-bar',
        canScrollLeft ? 'can-scroll-left' : '',
        canScrollRight ? 'can-scroll-right' : '',
      ].join(' ')}
    >
      <button
        type="button"
        className="scrollable-tabs-arrow scrollable-tabs-arrow--left"
        aria-label="Scroll tabs left"
        disabled={!canScrollLeft}
        onClick={() => scrollTabs(-1)}
      >
        <i className="bi bi-chevron-left" />
      </button>
      <Nav
        ref={tabsRef}
        variant="tabs"
        className="scrollable-tabs"
        onScroll={updateScrollArrows}
      >
        {tabs.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link
              active={activeKey === tab.key}
              onClick={() => onSelect(tab)}
              className="scrollable-tabs-link"
            >
              {tab.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <button
        type="button"
        className="scrollable-tabs-arrow scrollable-tabs-arrow--right"
        aria-label="Scroll tabs right"
        disabled={!canScrollRight}
        onClick={() => scrollTabs(1)}
      >
        <i className="bi bi-chevron-right" />
      </button>
    </div>
  );
};
